import * as React from 'react';
import {
  Control,
  Errors,
  // Field,
  LocalForm
} from 'react-redux-form';
import ReactDatePicker from 'react-datepicker';
import * as moment from 'moment-timezone';

import { getDefaultErrorMessages, hasDateValue, hasValue, isNotOverLength255 } from '@common/validators';
import { OptionsViewModel } from '@models/common.models';
import { dateParser } from '@common/dateParser';
import { AutoAuthoViewModel, SetTripStatusRequestViewModel } from '@services/AutoAuthoService';

// Defining the properties the component expects
// Consider this the "setup" of the component
// All properties should be readonly
export interface AutoAuthoStatusUpdateComponentProperties {
  readonly appointmentDate: moment.Moment | null;
  readonly cancel: () => void;
  readonly getTripReasons: (phoneNumber: string, swoId: number) => void;
  readonly phoneNumber: string;
  readonly reset: () => void;
  readonly selectedSwoTripStatus?: number;
  readonly selectSwoTripStatus: (status: number) => void;
  readonly setAppointmentDate: (date: moment.Moment | null) => void;
  readonly setTripStatus: (phoneNumber: string, swoId: number, request: SetTripStatusRequestViewModel) => void;
  readonly setSetTripStatusFormDispatcher: (dispatcher: Function) => void;
  readonly swoId: number;
  readonly swoTripStatusMap: object;
  readonly tripReasons?: OptionsViewModel[];
  readonly tripStatusUpdated: boolean;
  readonly updateViewModel: (viewModel: AutoAuthoViewModel) => void;
  readonly viewModel: AutoAuthoViewModel;
}

// Component definition
export class AutoAuthoStatusUpdateComponent extends React.Component<AutoAuthoStatusUpdateComponentProperties> {
  public componentDidUpdate(): void {
    if (this.props.phoneNumber && this.props.swoId) {
      if (!this.props.tripReasons) {
        this.props.getTripReasons(this.props.phoneNumber, this.props.swoId);
      }
    }
  }

  public componentWillUnmount(): void {
    this.props.reset();
  }

  public render(): JSX.Element {
    let statusIds: number[] = [];
    switch (this.props.viewModel.tripStatus || 0) {
      case 3:
        statusIds = [21];
        break;
      case 21:
        statusIds = [35, 37];
        break;
      case 35:
        statusIds = [37];
        break;
      case 37:
        statusIds = [21, 35];
        break;
      case 1:
      case 0:
      default:
        statusIds = [3, 21];
        break;
    }
    const statusOptions = !this.props.swoTripStatusMap ? (
      <option />
    ) : (
      Object.keys(this.props.swoTripStatusMap)
        .filter(k => statusIds.indexOf(+k) > -1)
        .map(k => (
          <option key={k} value={k}>
            {+k === 21 ? 'Set Appointment' : this.props.swoTripStatusMap[k]}
          </option>
        ))
    );

    const tripReasonOptions = !this.props.tripReasons ? (
      <option />
    ) : (
      this.props.tripReasons.filter(ovm => ovm.id !== 0).map(ovm => (
        <option key={ovm.value} value={ovm.id}>
          {ovm.value}
        </option>
      ))
    );

    let timePickerProps = {
      showTimeSelectOnly: true
    };
    if (!this.props.appointmentDate) {
      let propName = 'disabled';
      timePickerProps[propName] = true;
    } else {
      let propName = 'minTime';
      timePickerProps[propName] = this.props.appointmentDate.clone().add(15, 'minutes');
      propName = 'maxTime';
      timePickerProps[propName] = dateParser
        .now()
        .hours(23)
        .minutes(45);
    }

    return (
      <div className="row center-xs">
        <LocalForm
          className="col"
          getDispatch={d => this.props.setSetTripStatusFormDispatcher(d)}
          hideNativeErrors={true}
          onSubmit={m => {
            this.props.setTripStatus(this.props.phoneNumber, this.props.swoId, {
              ...m,
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || moment.tz.guess(),
              tripDateFrom: this.props.appointmentDate
                ? this.props.appointmentDate!.format('YYYY-MM-DDTHH:mm:00')
                : undefined,
              tripDateTo: m.tripDateTo ? m.tripDateTo!.format('YYYY-MM-DDTHH:mm:00') : undefined
            });
            this.props.updateViewModel({
              ...this.props.viewModel,
              tripStatus: +m.tripStatus === 37 ? (this.props.appointmentDate ? 21 : 1) : +m.tripStatus
            });
          }}
        >
          <h2>Update SWO Status</h2>
          {this.props.tripStatusUpdated ? (
            <h4>SWO #: {this.props.swoId} status was updated</h4>
          ) : (
            <h4>SWO #: {this.props.swoId}</h4>
          )}
          <h4>SWO Trip Status: {this.getTripStatusDescription(this.props.viewModel.tripStatus)}</h4>
          <br />
          <div className="form-control">
            <label>SWO Trip Status</label>
            <Control.select
              model=".tripStatus"
              onChange={e => this.props.selectSwoTripStatus(+e.currentTarget.value)}
              validators={{ required: hasValue }}
            >
              {!this.props.selectedSwoTripStatus && <option />}
              {statusOptions}
            </Control.select>
            <Errors
              className="error"
              model=".tripStatus"
              show={{ touched: true, focus: false }}
              messages={getDefaultErrorMessages()}
            />
          </div>
          {this.props.selectedSwoTripStatus === 37 && (
            <>
              <div className="form-control">
                <label>Reason for Follow-up</label>
                <Control.select model=".followupReason" validators={{ required: hasValue }}>
                  <option />
                  {tripReasonOptions}
                </Control.select>
                <Errors
                  className="error"
                  model=".followupReason"
                  show={{ touched: true, focus: false }}
                  messages={getDefaultErrorMessages()}
                />
              </div>
              <div className="col-sm form-control">
                <label>Estimated Completion Date</label>
                <Control
                  autoComplete="off"
                  model=".estimatedCompletionDate"
                  validators={this.props.appointmentDate ? { hasDateValue: hasDateValue } : {}}
                  validateOn="blur"
                  component={props => {
                    let val;
                    if (props.value && !moment.isMoment(props.value)) {
                      val = dateParser.parse(props.value);
                    } else {
                      // already a moment
                      val = props.value;
                    }

                    return (
                      <ReactDatePicker {...props} selected={val || null} value={val ? val.format('MM/DD/YYYY') : ''} />
                    );
                  }}
                />
                <Errors
                  className="error"
                  model=".estimatedCompletionDate"
                  show={{ touched: true, focus: false }}
                  messages={{ ...getDefaultErrorMessages(), validDate: 'Invalid Date' }}
                />
              </div>
            </>
          )}
          {this.props.selectedSwoTripStatus === 3 && (
            <div className="form-control">
              <label>Working Days to Wait for Customer Callback</label>
              <Control.text defaultValue="2" max="91" min="1" model=".callbackWaitDays" type="number" />
            </div>
          )}
          {(this.props.selectedSwoTripStatus === 21 || this.props.selectedSwoTripStatus === 37) && (
            <div className="row">
              <div className="col-sm form-control">
                <label>Appointment Start</label>
                <Control
                  model=".tripDateFrom"
                  // validators={this.props.selectedSwoTripStatus === 37 ? {} : { hasDateValue: hasDateValue }}
                  validateOn="blur"
                  component={props => (
                    <ReactDatePicker
                      {...props}
                      autoComplete="off"
                      dateFormat="MM/DD/YYYY hh:mm A"
                      onChange={m => {
                        this.props.setAppointmentDate(m);
                        props.onChange(m);
                      }}
                      selected={this.props.appointmentDate}
                      showTimeSelect={true}
                      timeIntervals={15}
                      value={this.props.appointmentDate ? this.props.appointmentDate.format('MM/DD/YYYY hh:mm A') : ''}
                    />
                  )}
                />
                {/*<Errors className="error" model=".tripDateFrom" show={{ touched: true, focus: false }} messages={{ ...getDefaultErrorMessages(), validDate: 'Invalid Date' }} />*/}
              </div>
              <div className="col-sm form-control">
                <label>Appointment End</label>
                <Control
                  model=".tripDateTo"
                  validators={
                    this.props.selectedSwoTripStatus === 21 || this.props.appointmentDate
                      ? { hasDateValue: hasDateValue }
                      : {}
                  }
                  validateOn="blur"
                  component={props => {
                    let val;
                    if (props.value && !moment.isMoment(props.value)) {
                      val = dateParser.parse(props.value);
                    } else {
                      // already a moment
                      val = props.value;
                    }

                    return (
                      <ReactDatePicker
                        {...props}
                        {...timePickerProps}
                        autoComplete="off"
                        dateFormat="LT"
                        selected={val || null}
                        showTimeSelect={true}
                        timeIntervals={15}
                        value={val ? val.format('LT') : ''}
                      />
                    );
                  }}
                />
                <Errors
                  className="error"
                  model=".tripDateTo"
                  show={{ touched: true, focus: false }}
                  messages={{ ...getDefaultErrorMessages(), validDate: 'Invalid Date' }}
                />
              </div>
            </div>
          )}
          <div>
            <label>Comments</label>
            <Control.textarea
              model=".comment"
              validators={{ required: hasValue, checkLength255: isNotOverLength255 }}
            />
            <Errors
              className="error"
              model=".comment"
              show={{ touched: true, focus: false }}
              messages={getDefaultErrorMessages()}
            />
          </div>
          <button className="btn btn-primary" type="submit">
            Save Changes
          </button>
          <button className="btn btn-secondary" onClick={() => this.props.cancel()} type="reset">
            Cancel
          </button>
        </LocalForm>
      </div>
    );
  }

  private getTripStatusDescription(statusId?: number): string {
    if (!this.props.swoTripStatusMap || !statusId) {
      return 'Appointment Pending';
    }
    return this.props.swoTripStatusMap[statusId.toString()];
  }
}



// WEBPACK FOOTER //
// ./src/auto-autho/_components/status-update/_components/status-update.component.tsx